import {
  ComponentFactoryResolver,
  Injectable,
  ViewContainerRef
} from '@angular/core';
import { Subject } from 'rxjs';
import { Tab } from '../model/tab';
import GenericModel from '../model/generic-model';
import { Project } from '../model/project/project';
import ComponentHelper from '../helpers/component-helper';
import { SubProject } from '../model/project/sub-project';

@Injectable({
  providedIn: 'root'
})
export class ProjectTabsService {
  private tabs: Tab[] = [];
  private activeTab: Tab;

  public tabClosed: Subject<any> = new Subject();
  public tabsChanged: Subject<any> = new Subject();

  projectTabContent: ViewContainerRef;

  constructor(private componentFactoryResolver: ComponentFactoryResolver) {}

  openProjectTab(
    model: SubProject,
    projectModel: Project,
    title: string,
    inputs: any,
    outputs: any
  ) {
    this.openInTab(
      title,
      ComponentHelper.getComponentClassFor(model),
      model,
      projectModel,
      inputs,
      outputs
    );
  }

  initializeTabsContent(projectTabContent: ViewContainerRef) {
    this.projectTabContent = projectTabContent;
    if (this.getActiveTab()) {
      this.insertComponent(this.getActiveTab());
    }
  }

  getTabContentRef() {
    return this.projectTabContent;
  }

  openInTab(
    title: string,
    componentClass: any,
    componentModel?: GenericModel,
    projectModel?: GenericModel,
    inputs?: any,
    outputs?: any
  ) {
    let activatedTab = this.pushTab(
      title,
      componentClass,
      componentModel,
      projectModel,
      inputs,
      outputs
    );
    this.insertComponent(activatedTab);
  }

  pushTab(
    title: string,
    componentClass: any,
    componentModel?: GenericModel,
    projectModel?: GenericModel,
    inputs?: any,
    outputs?: any
  ): Tab {
    const lookup = this.findExistingTab(componentClass, componentModel);
    // Do a lookup of the tab we are trying to register, and if we already have it, reuse it
    let activatedTab: Tab;
    if (lookup) {
      if (inputs) {
        Object.keys(inputs).map(key => {
          lookup.component.instance[key] = inputs[key];
        });
      }
      if (outputs) {
        Object.keys(outputs).map(key => {
          lookup.component.instance[key] = outputs[key];
        });
      }
      activatedTab = lookup;
    } else {
      activatedTab = new Tab(
        title,
        null,
        componentClass,
        componentModel,
        projectModel,
        inputs,
        outputs
      );
      activatedTab.createComponentView(
        this.componentFactoryResolver,
        this.projectTabContent
      );
      this.tabs.push(activatedTab);
    }

    return activatedTab;
  }

  insertComponent(tab: Tab): boolean {
    if (this.projectTabContent) {
      if (!tab.component || tab.component.hostView.destroyed) {
        tab.createComponentView(
          this.componentFactoryResolver,
          this.projectTabContent
        );
      }
      this.setActiveTab(tab);
      this.projectTabContent.detach();
      this.projectTabContent.clear();
      this.projectTabContent.insert(tab.component.hostView);
      // After inserting, make sure we update the component one more time
      tab.component.changeDetectorRef.markForCheck();
      return true;
    } else {
      console.error('Failed inserting project component, retrying...', tab);
      setTimeout(() => this.insertComponent(tab), 200);
      return false;
    }
  }

  private findExistingTab(componentClass: any, model: any) {
    return this.tabs.find(
      tab =>
        tab.component.componentType === componentClass &&
        (model ? tab.model && tab.model.id === model.id : !tab.model)
    );
  }

  getActiveTab(): Tab {
    return this.activeTab;
  }

  setActiveTab(tab: Tab) {
    this.activeTab = tab;
  }

  getTabs() {
    return this.tabs;
  }

  openTab(tab: Tab) {
    this.setActiveTab(tab);
    this.insertComponent(tab);
  }

  clearTabs() {
    if (this.tabs != null) {
      for (let tab of this.tabs) {
        if (tab != null) {
          tab.closeTab();
        }
      }
      this.tabs.length = 0;
    }
    this.activeTab = null;
    this.tabsChanged.next();
  }
}
